import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type { PaginatedResult } from 'src/shared/api/types';

interface CrudHooksApi<T, ListParams, CreatePayload, UpdatePayload, Id> {
  list: (params?: ListParams) => Promise<PaginatedResult<T>>;
  create: (data: CreatePayload) => Promise<T>;
  update: (id: Id, data: UpdatePayload) => Promise<T>;
  remove: (id: Id) => Promise<void>;
}

interface CrudHooksOptions {
  /**
   * Other caches a write to this resource makes stale: figures computed from
   * its rows elsewhere, which no key of its own would reach.
   */
  alsoInvalidate?: QueryKey[];
}

export const createCrudHooks = <
  T,
  ListParams,
  CreatePayload,
  UpdatePayload,
  Id extends string | number,
>(
  key: string,
  api: CrudHooksApi<T, ListParams, CreatePayload, UpdatePayload, Id>,
  options: CrudHooksOptions = {}
) => {
  const invalidate = (qc: QueryClient) =>
    Promise.all(
      [[key], ...(options.alsoInvalidate ?? [])].map((queryKey) =>
        qc.invalidateQueries({ queryKey })
      )
    );

  // The previous page stays on screen while the next search or page loads:
  // swapping a list for a spinner on every keystroke reads as flicker.
  const useList = (params?: ListParams) =>
    useQuery({
      queryKey: [key, params],
      queryFn: () => api.list(params),
      placeholderData: keepPreviousData,
    });

  const useCreate = () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (data: CreatePayload) => api.create(data),
      onSuccess: () => invalidate(qc),
    });
  };

  const useUpdate = () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ id, data }: { id: Id; data: UpdatePayload }) => api.update(id, data),
      onSuccess: () => invalidate(qc),
    });
  };

  const useDelete = () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (id: Id) => api.remove(id),
      onSuccess: () => invalidate(qc),
    });
  };

  return { useList, useCreate, useUpdate, useDelete };
};
